import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import { ICreateUnitOfMeasure } from "./interfaces/create-unit-measurement.interface";
import { IUpdateUnitOfMeasure } from "./interfaces/update-unit-measurement.interface";
import UnitMeasurementsAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/masters/unit-measurements/unit-measurements.entity";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { UnitMeasurementGroupEnum } from "../../material/material-group-unit-measurement/material-group-unit.enum";
const apiUrl = env.API_UNIT_MEASURE;
const moduleName = "unit_measurement";

@Injectable({
  providedIn: "root",
})
export class UnitMeasureService extends AbstractService<
  UnitMeasurementsAttributes,
  ICreateUnitOfMeasure,
  IUpdateUnitOfMeasure
> {
  
  /** The last filter used to load the list */
  currentFilter: CombinedFilter<UnitMeasurementsAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(filter: CombinedFilter<UnitMeasurementsAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
      const headers: Record<string, any> = {};
      // see order-customer-export for more info about this header
      // ctx: stands for context
      if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
      return this.http.get<IPaginationResult<UnitMeasurementsAttributes>>(API_URL, { headers });
  }

  /**
   * Obtiene unidades de medida con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Tres objetos (OR lógico) - busca en 'code', 'name', 'group.externalKeys.code'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param status Status filter
   * @param modeTree Tree mode (disables pagination)
   * @returns Paginated data
   *
   * Search fields: code, name, group.externalKeys.code
   * Array filters: status
   */
  findAllWithCount(
    options: IPagination<UnitMeasurementsAttributes>,
    status?: number[],
    modeTree: 'group' | null = null
  ): Observable<IPaginationResult<UnitMeasurementsAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<UnitMeasurementsAttributes>[] => {
      const deletedFilter: FilterFn<UnitMeasurementsAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        status && status.length > 0
          ? andWhere('statusId', Ops.in(...status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<UnitMeasurementsAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<UnitMeasurementsAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por GROUP EXTERNAL KEYS CODE (solo si hay búsqueda)
    const searchByGroupExternalCodeQueryObject: FilterFn<UnitMeasurementsAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("group.externalKeys.code" as keyof UnitMeasurementsAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<UnitMeasurementsAttributes>(
      // Expandimos los tres objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,
      ...searchByGroupExternalCodeQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      modeTree ? noop() : page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<UnitMeasurementsAttributes>>(API_URL);
  }
  /**
   * Pagination Combo
   * @param options Pagination options
   * @returns Paginated data
   */
  findToComboBoxPagination(
    options: IPagination<UnitMeasurementsAttributes>,
    groupCode?: UnitMeasurementGroupEnum
  ): Observable<IRequestResult<UnitMeasurementsAttributes[]>> {
    const query = buildQuery<UnitMeasurementsAttributes>(
      where('_deleted', Ops.eq(false)),
      andWhere("code", Ops.like(options.search ?? "", "i")),
      orWhere("name", Ops.like(options.search ?? "", "i")),
      orWhere("group.externalKeys.code" as keyof UnitMeasurementsAttributes, Ops.like(options.search ?? "", "i")),
      groupCode ? andAllWhere("group.code" as keyof UnitMeasurementsAttributes,Ops.eq(groupCode)) : noop(),
      andAllWhere("statusId", StatusEnum.ACTIVE),
      select(["code", "name"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<UnitMeasurementsAttributes[]>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(groupId?: string): Observable<IRequestResult<UnitMeasurementsAttributes[]>> {
    const query = buildQuery<UnitMeasurementsAttributes>(
      where('_deleted', Ops.eq(false)),
      groupId ? andWhere("group._id" as keyof UnitMeasurementsAttributes,Ops.eq(groupId)) : noop(),
      andAllWhere("statusId", StatusEnum.ACTIVE),
      select(["code", "name"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<UnitMeasurementsAttributes[]>>(API_URL);
  }
}
